// src/components/assessment/AssessmentStart.jsx
//
// The way into a check-in: one screen that is also the loading screen.
// It renders at once with the org's mark, the parts of the check-in and the
// privacy line, shows a spinner where the Start button will be, and enables
// Start when the config lands. It replaces the loader (fake status lines), the
// welcome card and the "Start Protocol" intro, which said the same things
// across three screens.
//
// Voice follows who is checking in (utils/resolveSurface.js): a youth player,
// a parent about their player, a family member, an employee, a community
// member, an athlete. Only the lead line and the privacy line change.

import React from 'react';
import { ShieldCheck, ChevronRight, Loader2, ClipboardList, SlidersHorizontal, Zap } from 'lucide-react';
import { useOrganization } from '../../context/OrganizationContext';
import { resolveAgeTier } from '../../utils/ageTier';
import { resolveMemberTypes } from '../../data/memberTypes';
import { resolveSurface } from '../../utils/resolveSurface';
import { isHubTheme } from '../../utils/orgTheme';
import { resolveSport } from '../../data/sports';

// Five seconds a question, rounded to whole minutes, never under one.
export const minutesFor = (questionCount) => Math.max(1, Math.round((questionCount * 5) / 60));

const voiceFor = (currentUser, orgData) => {
    const tier = resolveAgeTier(currentUser);
    if (tier === 'kid' || tier === 'teen') return 'youth';
    const slot = resolveMemberTypes(orgData).find((t) => t.id === currentUser?.memberType);
    const surface = resolveSurface({
        role: currentUser?.role, memberType: currentUser?.memberType, ageTier: tier,
        memberSurface: slot?.surface, youthSurface: orgData?.youthSurface
    });
    if (surface === 'parent') return 'parent';
    if (surface === 'family') return 'family';
    if (orgData?.industry === 'corporate') return 'employee';
    if (orgData?.industry === 'community') return 'community';
    if (orgData?.industry === 'responder') return 'member';
    return 'athlete';
};

const leadFor = (voice, { isPulseMode, returning, person = 'player' }) => {
    if (isPulseMode) return 'A few quick questions to keep your path current.';
    if (voice === 'youth') return 'No scores to beat and no wrong answers.';
    if (voice === 'parent') return `About your ${person}, in your words. It doesn't score them; it shows you what might be worth a conversation.`;
    if (voice === 'family') return 'About how you are doing, as the person doing the supporting.';
    return returning
        ? 'Updates your baseline, so your map and your path stay current.'
        : 'Sets your baseline, so everything after is shaped around you.';
};

const privacyFor = (voice, orgName, person = 'player') => {
    if (voice === 'youth') return 'Only you see this. Not your coach, your parents or your team.';
    if (voice === 'parent') return `Only you see this. Your ${person} never sees your answers, and you never see theirs.`;
    if (voice === 'family') return 'Only you see this. Not the person you support, and not the organization.';
    if (voice === 'employee') return 'Only you see this. Your employer never sees your answers.';
    if (voice === 'community') return 'Only you see this. Nobody in the program or the community can open it.';
    if (voice === 'member') return `Only you see this. Not your supervisor, your unit or ${orgName || 'the service'}.`;
    return `Only you see this. Not your coach, your team or ${orgName || 'your organization'}.`;
};

const Skeleton = ({ w = 'w-24' }) => <span className={`inline-block h-3 ${w} rounded bg-slate-200 animate-pulse align-middle`} />;

const AssessmentStart = ({ loading = false, config = null, currentUser, isPulseMode = false, onStart }) => {
    const { orgData } = useOrganization();
    const branding = orgData?.branding || {};
    const showLogo = !!(branding.enabled && branding.logoUrl);
    const orgName = orgData?.name || '';
    const hubName = (isHubTheme(orgData) && branding.hubName) || orgName;

    const voice = voiceFor(currentUser, orgData);
    const person = resolveSport(orgData).person || 'player';
    const returning = !!currentUser?.lastAssessmentDate;

    const screener = Array.isArray(config?.screener) ? config.screener : [];
    const questionCount = screener.reduce((n, s) => n + ((s.questions || []).length), 0);
    // Without a config yet, the parts list shows the shape of a full check-in;
    // a set that skips the radar or the focus test drops them when it lands.
    // A parent's check-in is about their player and never has either, and the
    // voice is known before the config, so a parent sees the right shape at once.
    const slimShape = voice === 'parent';
    const includeRadar = !isPulseMode && !slimShape && (!config || config?.configOptions?.includeRadar !== false);
    const includeGame = !isPulseMode && !slimShape && (!config || config?.configOptions?.includeCognitive !== false);
    const questionMinutes = config ? minutesFor(questionCount) : null;
    const totalMinutes = config ? questionMinutes + (includeRadar ? 1 : 0) + (includeGame ? 1 : 0) : null;

    const parts = [
        { icon: ClipboardList, title: 'Questions', meta: config ? `${questionCount} questions · about ${questionMinutes} min` : null },
        includeRadar ? { icon: SlidersHorizontal, title: 'Rate yourself', meta: 'Ten sliders · 1 min' } : null,
        includeGame ? { icon: Zap, title: 'Focus test', meta: 'A short reaction game · 1 min' } : null
    ].filter(Boolean);

    const title = isPulseMode ? 'Weekly check-in' : 'Your check-in';
    const ready = !loading && !!config;

    return (
        <div className="flex flex-col h-full bg-white overflow-hidden">
            <div className="flex-1 overflow-y-auto custom-scrollbar">
                <div className="min-h-full flex flex-col items-center justify-center p-6 md:p-10">
                    <div className="w-full max-w-md flex flex-col gap-5 animate-in fade-in duration-300">

                        <div className="flex items-center gap-3">
                            {showLogo ? (
                                <img src={branding.logoUrl} alt="" className="w-11 h-11 rounded-xl object-contain bg-white border border-slate-100" />
                            ) : (
                                <div className="w-11 h-11 rounded-xl bg-brand-primary text-white flex items-center justify-center font-black text-sm">
                                    {(hubName || 'E').slice(0, 2).toUpperCase()}
                                </div>
                            )}
                            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 truncate">{hubName}</p>
                        </div>

                        <div className="rounded-2xl p-5 text-white bg-brand-primary" style={{ borderLeft: '5px solid var(--brand-accent)' }}>
                            <h1 className="text-2xl md:text-3xl font-black tracking-tight mb-1">{title}</h1>
                            <p className="text-sm md:text-base leading-relaxed text-white/85">{leadFor(voice, { isPulseMode, returning, person })}</p>
                        </div>

                        <div className="flex flex-col gap-2">
                            {parts.map((p, i) => {
                                const Icon = p.icon;
                                return (
                                    <div key={p.title} className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
                                        <div className="w-8 h-8 rounded-full bg-brand-primary text-white flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-bold text-slate-800 leading-tight">{p.title}</p>
                                            <p className="text-xs text-slate-500 mt-0.5">{p.meta || <Skeleton w="w-32" />}</p>
                                        </div>
                                        <Icon size={16} className="text-slate-300 shrink-0" />
                                    </div>
                                );
                            })}
                            <p className="text-xs font-semibold text-slate-500 px-1 pt-1">
                                {totalMinutes ? `About ${totalMinutes} minute${totalMinutes === 1 ? '' : 's'} in all.` : <Skeleton w="w-28" />}
                                {' '}You can stop and come back.
                            </p>
                        </div>

                        <div className="flex items-start gap-2 text-sm text-slate-600 leading-relaxed">
                            <ShieldCheck size={18} className="text-brand-primary shrink-0 mt-0.5" />
                            <p>{((voice === 'youth' && resolveSport(orgData).id === 'school') ? 'Only you see this. Not your teachers, your parents or your school.' : privacyFor(voice, orgName, person))}</p>
                        </div>

                        {voice === 'youth' && (
                            <p className="text-xs text-slate-500 leading-relaxed">
                                Need someone right now? Kids Help Phone: text <strong>CONNECT</strong> to <strong>686868</strong>, or call or text <strong>9-8-8</strong>.
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="shrink-0 border-t border-slate-100 bg-white p-4 md:p-5">
                <div className="max-w-md mx-auto flex flex-col gap-2">
                    <button
                        onClick={ready ? onStart : undefined}
                        disabled={!ready}
                        className="w-full py-4 bg-brand-primary text-white rounded-xl font-bold hover:brightness-110 transition-all flex items-center justify-center gap-2 shadow-md disabled:opacity-70 disabled:cursor-wait"
                    >
                        {ready ? <>Start <ChevronRight size={18} /></> : <><Loader2 size={18} className="animate-spin" /> Loading your check-in</>}
                    </button>
                    <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                        A self-reflection tool. It is not a diagnosis and not a substitute for professional advice.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AssessmentStart;
